import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  type AdminCreateMemberRequest,
  type AdminMember,
  type AdminMemberCatalog,
  type AdminMemberDetail,
  type AdminUpdateMemberRequest,
  adminCreateMemberRequestSchema,
  discordSnowflakeSchema,
} from "@ternary/contracts"
import {
  Avatar,
  Badge,
  Button,
  breakable,
  Card,
  Checkbox,
  type Column,
  ConfirmDialog,
  DescriptionList,
  Dialog,
  EmptyState,
  Field,
  FormWizard,
  IconButton,
  Input,
  Menu,
  MultiSelect,
  Muted,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
  Table,
  useToast,
  type WizardErrors,
  WizardFieldError,
  type WizardStep,
  type WizardStepContext,
} from "@ternary/ui"
import { type FormEvent, type ReactElement, type ReactNode, useId, useRef, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import {
  AdminApiError,
  createAdminMember,
  deleteAdminMembers,
  loadAdminMember,
  loadAdminMembers,
  lookupAdminDiscordMember,
  updateAdminMember,
} from "../api.js"
import { DocsLink } from "../components/docs-link.js"
import { errorDetail, LoadError } from "../components/load-error.js"
import { NavIcon } from "../components/nav-icons.js"
import { useErrorsUntilEdited } from "../components/setup-page.js"
import { discordAvatarSrc } from "../customer.js"

const membersPerPage = 10

type Invoice = AdminMemberDetail["invoices"][number]
type Payment = AdminMemberDetail["payments"][number]

function money(amountMinor: number, currency: string) {
  return new Intl.NumberFormat("en-US", { currency, style: "currency" }).format(amountMinor / 100)
}

function memberName(member: AdminMember) {
  return member.firstName && member.lastName ? `${member.firstName} ${member.lastName}` : "n/a"
}

/*
 * On screen a missing value is a muted dash (a Table's empty cell, or `dash`). The "n/a" of
 * memberName and the CSV around it stay as they are: exports do not change (ruling C19).
 */
const dash = <Muted>—</Muted>

const REGION = new Intl.DisplayNames(["en"], { type: "region" })

/** A country code as its English name, such as "CA" as "Canada"; an unknown code as itself. */
function regionName(code: string) {
  try {
    return REGION.of(code) ?? code
  } catch {
    return code
  }
}

/** The member's full name, or null without one. */
function screenName(member: AdminMember) {
  return member.firstName && member.lastName ? `${member.firstName} ${member.lastName}` : null
}

/** A day as the admin reads it, or null without one. */
function day(value: string | null) {
  return value ? new Date(value).toLocaleDateString("en-US", { dateStyle: "medium" }) : null
}

/** A long code on one line, cut short with an ellipsis, its whole value in the title. */
function Code({ text }: { text: string }) {
  return (
    <span className="admin-mono admin-truncate" title={text}>
      {text}
    </span>
  )
}

/** The member's Discord username, or their email when no Discord account is bound. */
function handle(member: AdminMember) {
  return member.discord.username ?? member.customerEmail
}

/** A Stripe status such as "past_due" as "Past due". */
function humanize(value: string) {
  const text = value.replaceAll("_", " ")
  return text.charAt(0).toUpperCase() + text.slice(1)
}

function downloadCsv(filename: string, rows: string[][]) {
  const csv = rows
    .map((row) => row.map((value) => `"${value.replaceAll('"', '""')}"`).join(","))
    .join("\n")
  const link = document.createElement("a")
  link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }))
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}

function exportMembers(members: AdminMember[]) {
  downloadCsv("Users.csv", [
    [
      "Discord",
      "Name",
      "Discord ID",
      "Customer ID",
      "Customer Email",
      "License Key",
      "Referral Code",
      "Referrals",
    ],
    ...members.map((member) => [
      member.discord.username ?? "n/a",
      memberName(member),
      member.discord.id ?? "",
      member.customerId,
      member.customerEmail,
      member.key,
      member.referralCode,
      String(member.totalReferrals),
    ]),
  ])
}

/** A form in a dialog, with Cancel and its submit button in the footer. */
function FormDialog({
  children,
  error,
  onClose,
  onSubmit,
  saving,
  submitLabel,
  title,
}: {
  children: ReactNode
  error: string
  onClose(): void
  onSubmit(): void
  saving: boolean
  submitLabel: string
  title: string
}) {
  const formId = useId()
  return (
    <Dialog
      footer={
        <>
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button form={formId} loading={saving} type="submit">
            {submitLabel}
          </Button>
        </>
      }
      onClose={onClose}
      open
      size="lg"
      title={title}
    >
      <form
        className="admin-stack"
        id={formId}
        onSubmit={(event: FormEvent) => {
          event.preventDefault()
          onSubmit()
        }}
      >
        {children}
        {/* The page behind a modal is inert, so the request's error shows here. */}
        {error ? (
          <p className="tny-error" role="alert">
            {error}
          </p>
        ) : null}
      </form>
    </Dialog>
  )
}

/** What Add user's wizard edits: the create request itself, field for field. */
export type AddUserDraft = AdminCreateMemberRequest

const memberRequest = adminCreateMemberRequestSchema.shape

/** The request, its keys in the order the one-page form sent them. */
function addUserRequest(draft: AddUserDraft): AdminCreateMemberRequest {
  return {
    customerEmail: draft.customerEmail,
    customerId: draft.customerId,
    discordId: draft.discordId,
    key: draft.key,
    planId: draft.planId,
    roles: draft.roles,
    subscriptionId: draft.subscriptionId,
    subscriptionType: draft.subscriptionType,
  }
}

function memberErrors(draft: AddUserDraft): WizardErrors {
  const errors: WizardErrors = {}
  if (draft.discordId !== null && !discordSnowflakeSchema.safeParse(draft.discordId).success) {
    errors.discordId = "Enter a Discord ID of 15 to 22 digits, or leave it blank."
  }
  if (!memberRequest.customerEmail.safeParse(draft.customerEmail).success) {
    errors.customerEmail = "Enter an email address, such as member@northwind.example."
  }
  if (!draft.key.trim()) errors.key = "Enter a license key."
  return errors
}

function subscriptionErrors(draft: AddUserDraft): WizardErrors {
  const errors: WizardErrors = {}
  if (!memberRequest.customerId.safeParse(draft.customerId).success) {
    errors.customerId = "Enter a Stripe customer ID that starts with cus_."
  }
  if (!draft.subscriptionId.trim()) errors.subscriptionId = "Enter a Stripe subscription ID."
  if (!draft.planId) errors.planId = "Choose a product."
  // MultiSelect is a button, which cannot be `required`, so the step checks it.
  if (draft.roles.length === 0) errors.roles = "Choose at least one role."
  return errors
}

function MemberStep({ draft, errors, setDraft }: WizardStepContext<AddUserDraft>) {
  const shown = useErrorsUntilEdited(errors, draft)
  const [found, setFound] = useState("")
  const lookup = useMutation({
    mutationFn: lookupAdminDiscordMember,
    onError(error) {
      setFound(error instanceof Error ? error.message : "Discord member not found.")
    },
    onSuccess(profile) {
      setFound(`Found @${profile.username}`)
    },
  })
  return (
    <>
      <div className="admin-stack admin-stack-tight">
        <div className="admin-coupon-row">
          <Field
            error={shown.discordId}
            hint={shown.discordId ? undefined : "Leave it blank if they have none."}
            label="Discord ID"
          >
            <Input
              inputMode="numeric"
              onChange={(event) => {
                setFound("")
                setDraft({ discordId: event.target.value.trim() || null })
              }}
              placeholder="15–22 digits"
              value={draft.discordId ?? ""}
            />
          </Field>
          <Button
            disabled={!draft.discordId}
            loading={lookup.isPending}
            onClick={() => draft.discordId && lookup.mutate(draft.discordId)}
            variant="secondary"
          >
            Look up
          </Button>
        </div>
        <p aria-live="polite" className="admin-note">
          {found}
        </p>
      </div>
      <Field error={shown.customerEmail} label="Customer email" required>
        <Input
          autoComplete="off"
          onChange={(event) => setDraft({ customerEmail: event.target.value })}
          placeholder="member@northwind.example"
          type="email"
          value={draft.customerEmail}
        />
      </Field>
      <Field
        error={shown.key}
        hint={shown.key ? undefined : "Use a unique license key for each user."}
        label="License key"
        required
      >
        <Input onChange={(event) => setDraft({ key: event.target.value })} value={draft.key} />
      </Field>
    </>
  )
}

function SubscriptionStep({
  catalog,
  draft,
  errors,
  setDraft,
}: WizardStepContext<AddUserDraft> & { catalog: AdminMemberCatalog }) {
  const shown = useErrorsUntilEdited(errors, draft)
  return (
    <>
      {/* Side by side where they fit, so the step fits a 720px screen with its errors. */}
      <div className="admin-field-grid">
        <Field
          error={shown.customerId}
          hint={shown.customerId ? undefined : "From Stripe → Customers."}
          label="Customer ID"
          required
        >
          <Input
            onChange={(event) => setDraft({ customerId: event.target.value })}
            placeholder="cus_…"
            value={draft.customerId}
          />
        </Field>
        <Field
          error={shown.subscriptionId}
          hint={shown.subscriptionId ? undefined : "From Stripe → Subscriptions."}
          label="Subscription ID"
          required
        >
          <Input
            onChange={(event) => setDraft({ subscriptionId: event.target.value })}
            placeholder="sub_…"
            value={draft.subscriptionId}
          />
        </Field>
      </div>
      <div className="admin-field-pair">
        <Field
          error={shown.planId}
          hint={shown.planId || !draft.planId ? undefined : `Price ID: ${draft.planId}`}
          label="Product"
          required
        >
          <Select
            onChange={(event) => setDraft({ planId: event.target.value })}
            value={draft.planId}
          >
            {catalog.products.map((product) => (
              <option key={product.id} value={product.planId}>
                {product.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Subscription type">
          <Select
            onChange={(event) =>
              setDraft({ subscriptionType: event.target.value as AddUserDraft["subscriptionType"] })
            }
            value={draft.subscriptionType}
          >
            <option value="recurring">Recurring</option>
            <option value="lifetime">Lifetime</option>
            <option value="manual">Manual</option>
          </Select>
        </Field>
      </div>
      <MultiSelect
        error={shown.roles}
        label="Discord roles"
        onChange={(roles) => setDraft({ roles })}
        options={catalog.roles.map((role) => ({ label: role.name, value: role.id }))}
        required
        value={draft.roles}
      />
    </>
  )
}

/** The wizard, once the catalog it offers products and roles from has loaded. */
function AddUserWizard({
  initialDraft,
  onClose,
  onCreated,
  stepId,
}: {
  initialDraft: Partial<AddUserDraft> | undefined
  onClose(): void
  onCreated: (() => void) | undefined
  stepId: string | undefined
}) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const query = useQuery({ queryFn: loadAdminMembers, queryKey: ["admin-members"] })
  const catalog = query.data
  if (!catalog) {
    return query.isError ? (
      <LoadError
        detail={errorDetail(query.error)}
        message="Users could not load"
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    ) : (
      <p className="admin-note" role="status">
        Loading…
      </p>
    )
  }
  const steps: WizardStep<AddUserDraft>[] = [
    {
      fields: ["discordId", "customerEmail", "key"],
      id: "member",
      render: (context) => <MemberStep {...context} />,
      title: "Member",
      validate: memberErrors,
    },
    {
      fields: ["customerId", "subscriptionId", "planId", "subscriptionType", "roles"],
      id: "subscription",
      render: (context) => <SubscriptionStep catalog={catalog} {...context} />,
      title: "Subscription",
      validate: subscriptionErrors,
    },
  ]
  return (
    <FormWizard<AddUserDraft>
      headingLevel={2}
      initialDraft={{
        customerEmail: "",
        customerId: "",
        discordId: null,
        key: "",
        planId: catalog.products[0]?.planId ?? "",
        roles: [],
        subscriptionId: "",
        subscriptionType: "recurring",
        ...initialDraft,
      }}
      onSubmit={async (draft) => {
        try {
          await createAdminMember(addUserRequest(draft))
        } catch (error) {
          // A key another user has: back to Member, with the API's words on License key.
          if (
            error instanceof AdminApiError &&
            error.status === 409 &&
            /license key is already in use/i.test(error.message)
          ) {
            throw new WizardFieldError("member", { key: error.message })
          }
          throw error
        }
        // The first member is on the dashboard's setup checklist (G7).
        void queryClient.invalidateQueries({ queryKey: ["admin-setup"] })
        await queryClient.invalidateQueries({ queryKey: ["admin-members"] })
        await queryClient.invalidateQueries({ queryKey: ["admin-dashboard"] })
        toast({ message: `${draft.customerEmail} added.`, tone: "success" })
        onCreated?.()
        onClose()
      }}
      steps={steps}
      stepId={stepId}
      submitLabel="Save"
      title="User details"
      // The dialog's own title says what this is; the wizard's names its form.
      titleHidden
    />
  )
}

/**
 * Add user, in two steps so neither scrolls: Member (the Discord ID, email and license key), then
 * Subscription (the Stripe customer and subscription, the product and the Discord roles). Save
 * sends the same request the one-page form did. `stepId` and `initialDraft` open a given step, as
 * the stories do.
 */
export function AddUserDialog({
  initialDraft,
  onClose,
  onCreated,
  open,
  stepId,
}: {
  open: boolean
  onClose(): void
  /** Called after a user was added, before the dialog closes. */
  onCreated?: (() => void) | undefined
  stepId?: string | undefined
  initialDraft?: Partial<AddUserDraft> | undefined
}): ReactElement {
  return (
    <Dialog onClose={onClose} open={open} size="lg" title="Add user">
      <AddUserWizard
        initialDraft={initialDraft}
        onClose={onClose}
        onCreated={onCreated}
        stepId={stepId}
      />
    </Dialog>
  )
}

function EditMemberForm({
  error,
  member,
  onClose,
  onSave,
  saving,
}: {
  error: string
  member: AdminMember
  onClose(): void
  onSave(member: AdminUpdateMemberRequest): void
  saving: boolean
}) {
  const [fields, setFields] = useState<AdminUpdateMemberRequest>({
    customerEmail: member.customerEmail,
    customerId: member.customerId,
    discordId: member.discord.id,
    totalReferrals: member.totalReferrals,
  })
  return (
    <FormDialog
      error={error}
      onClose={onClose}
      onSubmit={() => onSave(fields)}
      saving={saving}
      submitLabel="Save changes"
      title={`Edit ${handle(member)}`}
    >
      <div className="admin-field-grid">
        <Field label="Customer ID" required>
          <Input
            onChange={(event) => setFields({ ...fields, customerId: event.target.value })}
            value={fields.customerId}
          />
        </Field>
        <Field label="Customer email" required>
          <Input
            onChange={(event) => setFields({ ...fields, customerEmail: event.target.value })}
            type="email"
            value={fields.customerEmail}
          />
        </Field>
        <Field label="Discord ID">
          <Input
            inputMode="numeric"
            onChange={(event) =>
              setFields({ ...fields, discordId: event.target.value.trim() || null })
            }
            value={fields.discordId ?? ""}
          />
        </Field>
        <Field hint="Adjust only to correct a count." label="Referral count" required>
          <Input
            min="0"
            onChange={(event) =>
              setFields({ ...fields, totalReferrals: Number(event.target.value) })
            }
            type="number"
            value={fields.totalReferrals}
          />
        </Field>
      </div>
    </FormDialog>
  )
}

function memberMatches(member: AdminMember, search: string) {
  const haystack = [
    member.discord.username,
    member.firstName,
    member.lastName,
    member.discord.id,
    member.customerId,
    member.customerEmail,
    member.key,
    member.referralCode,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
  return haystack.includes(search.trim().toLowerCase())
}

export function AdminUsersPage() {
  const queryClient = useQueryClient()
  const headingRef = useRef<HTMLHeadingElement>(null)
  const catalogQuery = useQuery({ queryFn: loadAdminMembers, queryKey: ["admin-members"] })
  const [search, setSearch] = useState("")
  const [pageIndex, setPageIndex] = useState(0)
  const [selected, setSelected] = useState<string[]>([])
  const [modal, setModal] = useState<"create" | "edit" | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [actionError, setActionError] = useState("")
  const catalog = catalogQuery.data
  const members = (catalog?.members ?? []).filter((member) => memberMatches(member, search))
  const selectedMember = catalog?.members.find((member) => member.id === selected[0])

  async function refresh() {
    setSelected([])
    setModal(null)
    setActionError("")
    setPageIndex(0)
    await queryClient.invalidateQueries({ queryKey: ["admin-members"] })
    await queryClient.invalidateQueries({ queryKey: ["admin-dashboard"] })
  }

  const update = useMutation({
    mutationFn: (fields: AdminUpdateMemberRequest) =>
      selectedMember
        ? updateAdminMember(selectedMember.id, fields)
        : Promise.reject(new Error("Select a user.")),
    async onSuccess() {
      await refresh()
      // The save cleared the selection, so the Edit button that opened the dialog is gone
      // and focus goes to the page heading, as after a delete.
      headingRef.current?.focus()
    },
  })
  const remove = useMutation({
    mutationFn: deleteAdminMembers,
    async onSuccess() {
      // Closed first, as on coupons: the page behind an open modal is inert and takes no focus.
      setConfirmDelete(false)
      // The first member is on the dashboard's setup checklist (G7).
      void queryClient.invalidateQueries({ queryKey: ["admin-setup"] })
      await refresh()
      // The selected rows' checkboxes left with them, so focus goes to the page heading.
      headingRef.current?.focus()
    },
    onError(error) {
      setConfirmDelete(false)
      setActionError(error instanceof Error ? error.message : "Users could not be deleted.")
    },
  })

  const writable = Boolean(catalog?.writable)
  const pageCount = Math.max(1, Math.ceil(members.length / membersPerPage))
  const currentPage = Math.min(pageIndex, pageCount - 1)
  const visibleMembers = members.slice(
    currentPage * membersPerPage,
    (currentPage + 1) * membersPerPage,
  )
  const firstEntry = members.length === 0 ? 0 : currentPage * membersPerPage + 1
  const lastEntry = Math.min((currentPage + 1) * membersPerPage, members.length)
  const allSelected =
    visibleMembers.length > 0 && visibleMembers.every((member) => selected.includes(member.id))

  function toggle(id: string) {
    setSelected((current) =>
      current.includes(id) ? current.filter((each) => each !== id) : [...current, id],
    )
  }

  function openCreate() {
    setActionError("")
    setModal("create")
  }

  const addButton = (
    <Button disabled={!writable} onClick={openCreate}>
      <NavIcon name="plus" />
      Add user
    </Button>
  )

  const columns: Column<AdminMember>[] = [
    {
      // The checkbox sits in the row header, beside the user it selects. A user without a
      // Discord account is linked by their email.
      cell: (member) => (
        <span className="admin-select-cell">
          <input
            aria-label={`Select ${member.customerEmail}`}
            checked={selected.includes(member.id)}
            className="tny-choice-input"
            onChange={() => toggle(member.id)}
            type="checkbox"
          />
          <Avatar
            name={handle(member)}
            size="sm"
            src={discordAvatarSrc(member.discord.avatarUrl)}
          />
          <Link to={`/dashboard/users/${member.id}`}>
            {member.discord.username ?? breakable(member.customerEmail)}
          </Link>
        </span>
      ),
      header: "Discord",
      key: "discord",
    },
    { cell: screenName, header: "Name", key: "name", priority: 2 },
    { cell: (member) => breakable(member.customerEmail), header: "Customer email", key: "email" },
    // The codes show from 1024px, each one line cut short: the profile has them in full.
    {
      cell: (member) => <Code text={member.key} />,
      header: "License key",
      key: "key",
      priority: 3,
    },
    {
      cell: (member) => (member.discord.id ? <Code text={member.discord.id} /> : null),
      header: "Discord ID",
      key: "discordId",
      priority: 3,
    },
    {
      cell: (member) => <Code text={member.customerId} />,
      header: "Customer ID",
      key: "customerId",
      priority: 3,
    },
    {
      cell: (member) => member.referralCode,
      header: "Referral code",
      key: "referral",
      priority: 3,
    },
    {
      align: "end",
      cell: (member) => String(member.totalReferrals),
      header: "Referrals",
      key: "referrals",
      priority: 3,
    },
  ]

  return (
    <section className="admin-stack">
      <PageHeader
        actions={
          <>
            <DocsLink />
            {/* Disabled while the users load or could not; the empty state has its own. */}
            {catalog?.members.length === 0 ? null : addButton}
          </>
        }
        description={
          catalog
            ? `${catalog.members.length} ${catalog.members.length === 1 ? "user" : "users"}`
            : undefined
        }
        headingRef={headingRef}
        title="Users"
      />
      {catalogQuery.isError ? (
        <LoadError
          detail={errorDetail(catalogQuery.error)}
          message="Users could not load"
          onRetry={() => void catalogQuery.refetch()}
          retrying={catalogQuery.isFetching}
        />
      ) : catalog && catalog.members.length === 0 ? (
        <EmptyState
          action={writable ? addButton : undefined}
          description="Users are the members who bought a product. Add one by hand to give them a license key and their Discord roles."
          title="Add your first user"
        />
      ) : (
        <>
          {catalog && !writable ? (
            <p className="admin-note">
              Changing users needs a connected Stripe account and Discord server.
            </p>
          ) : null}
          <div className="admin-toolbar">
            <SearchInput
              label="Search users"
              onSearch={(value) => {
                setSearch(value)
                setPageIndex(0)
              }}
              value={search}
            />
            <div className="admin-actions">
              <Checkbox
                checked={allSelected}
                disabled={visibleMembers.length === 0}
                label="Select all"
                onChange={() =>
                  setSelected(allSelected ? [] : visibleMembers.map((member) => member.id))
                }
              />
              <p className="admin-note" role="status">
                {selected.length > 0 ? `${selected.length} selected` : ""}
              </p>
              {/* With a selection they say what they do; Edit takes exactly one user. */}
              {selected.length > 0 ? (
                <>
                  <Button
                    disabled={!writable || selected.length !== 1}
                    onClick={() => {
                      setActionError("")
                      update.reset()
                      setModal("edit")
                    }}
                    variant="secondary"
                  >
                    <NavIcon name="edit" />
                    Edit
                  </Button>
                  <Button
                    disabled={!writable}
                    onClick={() => {
                      setActionError("")
                      setConfirmDelete(true)
                    }}
                    variant="danger"
                  >
                    <NavIcon name="trash" />
                    Delete
                  </Button>
                </>
              ) : (
                <>
                  <IconButton
                    disabled
                    icon={<NavIcon name="edit" />}
                    label="Edit selected user"
                    variant="secondary"
                  />
                  <IconButton
                    disabled
                    icon={<NavIcon name="trash" />}
                    label="Delete selected users"
                    variant="secondary"
                  />
                </>
              )}
              <IconButton
                disabled={!catalog}
                icon={<NavIcon name="download" />}
                label="Export users"
                onClick={() => catalog && exportMembers(catalog.members)}
                variant="secondary"
              />
            </div>
          </div>
          {actionError ? (
            <p className="tny-error" role="alert">
              {actionError}
            </p>
          ) : null}
          <Table
            caption="Users"
            captionHidden
            columns={columns}
            empty={<p className="admin-note">No users match “{search.trim()}”.</p>}
            loading={catalogQuery.isPending}
            loadingLabel="Loading users…"
            rowKey={(member) => member.id}
            rows={visibleMembers}
          />
          {members.length > membersPerPage ? (
            <Pagination
              hasNext={currentPage < pageCount - 1}
              hasPrevious={currentPage > 0}
              label="Users pages"
              onNext={() => setPageIndex(currentPage + 1)}
              onPrevious={() => setPageIndex(currentPage - 1)}
              summary={`Showing ${firstEntry} to ${lastEntry} of ${members.length} entries`}
            />
          ) : null}
        </>
      )}

      <AddUserDialog
        onClose={() => setModal(null)}
        onCreated={() => {
          setSelected([])
          setPageIndex(0)
          setActionError("")
        }}
        open={modal === "create"}
      />
      {modal === "edit" && selectedMember ? (
        <EditMemberForm
          error={update.error instanceof Error ? update.error.message : ""}
          member={selectedMember}
          onClose={() => setModal(null)}
          onSave={(member) => update.mutate(member)}
          saving={update.isPending}
        />
      ) : null}
      <ConfirmDialog
        body={
          selected.length === 1
            ? "Their license key stops working and they lose the Discord roles their products gave them."
            : "Their license keys stop working and they lose the Discord roles their products gave them."
        }
        confirmLabel={selected.length === 1 ? "Delete user" : `Delete ${selected.length} users`}
        danger
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => remove.mutate(selected)}
        open={confirmDelete}
        pending={remove.isPending}
        title={
          selected.length === 1
            ? // A selected id whose row a refetch removed still counts as one user.
              selectedMember
              ? `Delete ${handle(selectedMember)}?`
              : "Delete 1 user?"
            : `Delete ${selected.length} users?`
        }
      />
    </section>
  )
}

export function AdminUserProfilePage() {
  const { id = "" } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const detailQuery = useQuery({
    queryFn: ({ signal }) => loadAdminMember(id, { signal }),
    queryKey: ["admin-member", id],
  })
  const [editOpen, setEditOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [actionError, setActionError] = useState("")
  const update = useMutation({
    mutationFn: (fields: AdminUpdateMemberRequest) => updateAdminMember(id, fields),
    async onSuccess() {
      setEditOpen(false)
      await queryClient.invalidateQueries({ queryKey: ["admin-member", id] })
      await queryClient.invalidateQueries({ queryKey: ["admin-members"] })
    },
  })
  const remove = useMutation({
    mutationFn: () => deleteAdminMembers([id]),
    async onSuccess() {
      // The first member is on the dashboard's setup checklist (G7).
      void queryClient.invalidateQueries({ queryKey: ["admin-setup"] })
      await queryClient.invalidateQueries({ queryKey: ["admin-members"] })
      navigate("/dashboard/users")
    },
    onError(error) {
      setConfirmDelete(false)
      setActionError(error instanceof Error ? error.message : "User could not be deleted.")
    },
  })

  const back = (
    <Link to="/dashboard/users">
      <NavIcon name="back" />
      Users
    </Link>
  )

  if (detailQuery.isPending) {
    return (
      <section aria-busy="true" className="admin-stack admin-page">
        <PageHeader back={back} title="User" />
        <p className="admin-note" role="status">
          Loading user profile…
        </p>
      </section>
    )
  }
  if (detailQuery.isError) {
    return (
      <section className="admin-stack admin-page">
        <PageHeader back={back} title="User profile" />
        <LoadError
          detail={errorDetail(detailQuery.error)}
          message="The user profile could not load"
          onRetry={() => void detailQuery.refetch()}
          retrying={detailQuery.isFetching}
        />
      </section>
    )
  }
  const detail = detailQuery.data
  const { member } = detail
  const name = screenName(member)

  const invoiceColumns: Column<Invoice>[] = [
    {
      cell: (invoice) =>
        invoice.hostedUrl ? (
          <a href={invoice.hostedUrl} rel="noreferrer" target="_blank">
            {invoice.number}
          </a>
        ) : (
          invoice.number
        ),
      header: "Invoice",
      key: "number",
    },
    {
      align: "end",
      cell: (invoice) => money(invoice.amountDueMinor, invoice.currency),
      header: "Amount",
      key: "amount",
    },
    {
      cell: (invoice) =>
        invoice.paid ? <Badge tone="success">Paid</Badge> : <Badge>Pending</Badge>,
      header: "Status",
      key: "status",
    },
    { cell: (invoice) => day(invoice.dueAt), header: "Due", key: "due", priority: 2 },
    { cell: (invoice) => day(invoice.createdAt), header: "Created", key: "created", priority: 3 },
    {
      align: "end",
      cell: (invoice) =>
        invoice.pdfUrl ? (
          <a href={invoice.pdfUrl} rel="noreferrer" target="_blank">
            Download
          </a>
        ) : null,
      header: "PDF",
      key: "pdf",
    },
  ]
  const paymentColumns: Column<Payment>[] = [
    { cell: (payment) => payment.description, header: "Description", key: "description" },
    {
      align: "end",
      cell: (payment) => money(payment.amountReceivedMinor, payment.currency),
      header: "Paid",
      key: "paid",
    },
    {
      cell: (payment) => (
        <Badge tone={payment.status === "succeeded" ? "success" : "neutral"}>
          {humanize(payment.status)}
        </Badge>
      ),
      header: "Status",
      key: "status",
    },
    { cell: (payment) => day(payment.createdAt), header: "Date", key: "date", priority: 2 },
  ]

  return (
    <section className="admin-stack admin-page">
      <PageHeader
        actions={
          <Menu
            items={[
              {
                label: "Edit user",
                onSelect: () => {
                  update.reset()
                  setEditOpen(true)
                },
              },
              { label: "Export user", onSelect: () => exportMembers([member]) },
              {
                danger: true,
                label: "Delete user",
                onSelect: () => {
                  setActionError("")
                  setConfirmDelete(true)
                },
              },
            ]}
            label="Actions"
          />
        }
        back={back}
        description={name ? `${name} · ${member.customerEmail}` : member.customerEmail}
        media={
          <Avatar
            name={handle(member)}
            size="lg"
            src={discordAvatarSrc(member.discord.avatarUrl)}
          />
        }
        title={handle(member)}
      />
      {actionError ? (
        <p className="tny-error" role="alert">
          {actionError}
        </p>
      ) : null}

      <div className="admin-record">
        <Card title="Account details">
          <DescriptionList
            items={[
              { term: "License key", value: <span className="admin-mono">{member.key}</span> },
              { term: "Customer ID", value: member.customerId },
              { term: "Discord ID", value: member.discord.id ?? dash },
              { term: "Referral code", value: member.referralCode || dash },
              { term: "Total referrals", value: member.totalReferrals },
            ]}
          />
        </Card>
        {member.address ? (
          <Card title="Address">
            <DescriptionList
              items={[
                { term: "Line 1", value: member.address.line1 || dash },
                { term: "Line 2", value: member.address.line2 || dash },
                { term: "Postal code", value: member.address.postalCode || dash },
                { term: "City", value: member.address.city || dash },
                { term: "State", value: member.address.state || dash },
                {
                  term: "Country",
                  value: member.address.country ? regionName(member.address.country) : dash,
                },
              ]}
            />
          </Card>
        ) : null}
        {member.subscriptions.map((subscription) => (
          <Card key={subscription.id} title="Subscription">
            <DescriptionList
              items={[
                {
                  term: "Status",
                  value: (
                    <Badge tone={subscription.status === "active" ? "success" : "neutral"}>
                      {subscription.status === "cancelling"
                        ? "Cancelling at the end of the period"
                        : humanize(subscription.status)}
                    </Badge>
                  ),
                },
                {
                  term: "Price",
                  // A price the data lacks is a dash; the interval it is billed on still shows.
                  value: (
                    <>
                      {subscription.priceMinor !== null && subscription.currency
                        ? money(subscription.priceMinor, subscription.currency)
                        : dash}
                      {subscription.interval
                        ? subscription.interval === "one_time"
                          ? ", once"
                          : ` every ${subscription.intervalCount === 1 ? "" : `${subscription.intervalCount} `}${subscription.interval}${subscription.intervalCount === 1 ? "" : "s"}`
                        : ""}
                    </>
                  ),
                },
                { term: "Started", value: day(subscription.startedAt) ?? dash },
                // The data has no period end, so no range is made up.
                {
                  term: "Current period started",
                  value: day(subscription.currentPeriodStart) ?? dash,
                },
                { term: "Next invoice", value: day(subscription.nextInvoiceAt) ?? dash },
                { term: "Trial ends", value: day(subscription.trialEndsAt) ?? dash },
                { term: "Subscription ID", value: subscription.id },
                { term: "Price ID", value: subscription.planId },
              ]}
            />
          </Card>
        ))}
      </div>

      <Card title="Invoices">
        <Table
          caption="Invoices"
          captionHidden
          columns={invoiceColumns}
          empty={<p className="admin-note">No invoices yet.</p>}
          rowKey={(invoice) => invoice.id}
          rows={detail.invoices}
        />
      </Card>
      <Card title="Payments">
        <Table
          caption="Payments"
          captionHidden
          columns={paymentColumns}
          empty={<p className="admin-note">No payments yet.</p>}
          rowKey={(payment) => payment.id}
          rows={detail.payments}
        />
      </Card>

      {editOpen ? (
        <EditMemberForm
          error={update.error instanceof Error ? update.error.message : ""}
          member={member}
          onClose={() => setEditOpen(false)}
          onSave={(fields) => update.mutate(fields)}
          saving={update.isPending}
        />
      ) : null}
      <ConfirmDialog
        body="Their license key stops working and they lose the Discord roles their products gave them."
        confirmLabel="Delete user"
        danger
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => remove.mutate()}
        open={confirmDelete}
        pending={remove.isPending}
        title={`Delete ${handle(member)}?`}
      />
    </section>
  )
}
